import { useMutation, useQueryClient } from '@tanstack/react-query';

import { actionItemDismissal, useActionItemsByNoteQuery } from 'views/household/HouseholdNotes/action-items-query';
import {
    suggestedOpportunityDismissal,
    useSuggestedOpportunitiesByNoteQuery,
} from 'views/opportunities/suggested-opportunity-queries';
import { suggestedPersonDismissal, useSuggestedPeopleByNoteQuery } from 'views/person/suggested-person-queries';

import { NoteSuggestion, toNoteSuggestions } from './note-suggestion-utils';
import { suggestedMeetingDismissal, useSuggestedMeetingsByNoteQuery } from './suggested-meeting-queries';

export const useNoteSuggestions = (noteGUID: string) => {
    const actionItems = useActionItemsByNoteQuery(noteGUID);
    const suggestedOpportunities = useSuggestedOpportunitiesByNoteQuery(noteGUID);
    const suggestedPeople = useSuggestedPeopleByNoteQuery(noteGUID);
    const suggestedMeetings = useSuggestedMeetingsByNoteQuery(noteGUID);
    const sources = [actionItems, suggestedOpportunities, suggestedPeople, suggestedMeetings];

    return {
        data: toNoteSuggestions(
            actionItems.data ?? [],
            suggestedOpportunities.data ?? [],
            suggestedPeople.data ?? [],
            suggestedMeetings.data ?? [],
        ),
        isLoading: sources.some((source) => source.isLoading),
        // Each source degrades on its own; the list is unusable only when every source failed.
        isError: sources.every((source) => source.isError),
    };
};

// One mutation for every suggestion type so a row does not have to call four hooks. Only the
// dismissed type's by-note query is invalidated; useNoteSuggestions recombines the rest.
export const useDismissNoteSuggestion = (noteGUID: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (suggestion: NoteSuggestion) => dismissalFor(suggestion, noteGUID).request(),
        // Returning the invalidation keeps the mutation pending until the refetch lands, so a row
        // hidden while pending never flashes back before the fresh list drops it.
        onSuccess: (_, suggestion) =>
            queryClient.invalidateQueries({ queryKey: dismissalFor(suggestion, noteGUID).staleQueryKey }),
    });
};

// Each module owns its endpoint and stale key; this only picks the one for the suggestion's type.
const dismissalFor = (suggestion: NoteSuggestion, noteGUID: string) => {
    switch (suggestion.type) {
        case 'TASK':
            return actionItemDismissal(suggestion.actionItem.actionItemGUID, noteGUID);
        case 'OPPORTUNITY':
            return suggestedOpportunityDismissal(suggestion.suggestedOpportunity.suggestedOpportunityGUID, noteGUID);
        case 'PERSON':
            return suggestedPersonDismissal(suggestion.suggestedPerson.suggestedPersonGUID, noteGUID);
        case 'MEETING':
            return suggestedMeetingDismissal(suggestion.suggestedMeeting.suggestedMeetingGUID, noteGUID);
    }
};
